<!--
  Passport grid canvas: 12-column guide overlay, row list, and add-row control.
-->
<script setup lang="ts">
import { VIcons } from '@/shared/ui'
import type { DragPayload, ObjectInventoryField } from '../model/types'
import type { GridFieldType } from '@/features/Inventory/ObjectTypePassportGrid/model/types'
import type { GridRowGroup } from '../model/useGridOperations'
import PassportBuilderGridRow from './PassportBuilderGridRow.vue'

withDefaults(
  defineProps<{
    rows: GridRowGroup[]
    dropTargetRow?: number | null
    allFields?: ObjectInventoryField[]
    dragPayload?: DragPayload | null
    catalogTypeId?: number
  }>(),
  {
    dropTargetRow: null,
    allFields: () => [],
    dragPayload: null,
    catalogTypeId: 0,
  },
)

type GridFieldRemovePayload = { id: string; row: number; cell: number }
type GridFieldResizePayload = GridFieldRemovePayload & { newSpan: number }
type HeadingRenamePayload = GridFieldRemovePayload & { name: string }
type FieldSettingsPayload = GridFieldRemovePayload & { mode: 'catalog' | 'reference' }
type ReorderFieldPayload = GridFieldRemovePayload & { targetIndex: number }

const emit = defineEmits<{
  drop: [rowNum: number, event: DragEvent]
  'remove-field': [payload: GridFieldRemovePayload]
  'resize-field': [payload: GridFieldResizePayload]
  'rename-heading': [payload: HeadingRenamePayload]
  'rename-field': [payload: HeadingRenamePayload]
  'field-settings': [payload: FieldSettingsPayload]
  'remove-row': [rowNum: number]
  'add-row': []
  'drag-field-start': [payload: DragPayload, event: DragEvent]
  'drag-over-row': [rowNum: number]
  reorder: [rowNum: number, payload: ReorderFieldPayload]
  'patch-table-field': [fieldId: string, updater: (f: GridFieldType) => GridFieldType]
  'builder-drag-end': []
  'summary-field-settings': [payload: { tableFieldId: string; layoutItemId: string }]
}>()
</script>

<template>
  <div class="pb-grid-container">
    <div class="pb-grid-overlay" aria-hidden="true">
      <div v-for="n in 12" :key="n" class="pb-col-line" />
    </div>
    <div class="pb-grid-rows">
      <PassportBuilderGridRow
        v-for="g in rows"
        :key="g.row"
        :row-num="g.row"
        :items="g.items"
        :is-drop-target="dropTargetRow === g.row"
        :all-fields="allFields"
        :active-drag="dragPayload"
        :catalog-type-id="catalogTypeId"
        @drop="(rowNum, ev) => emit('drop', rowNum, ev)"
        @remove-field="emit('remove-field', $event)"
        @resize-field="emit('resize-field', $event)"
        @rename-heading="emit('rename-heading', $event)"
        @rename-field="emit('rename-field', $event)"
        @field-settings="emit('field-settings', $event)"
        @remove-row="emit('remove-row', $event)"
        @drag-field-start="(p, e) => emit('drag-field-start', p, e)"
        @drag-over-row="emit('drag-over-row', $event)"
        @reorder="(row, payload) => emit('reorder', row, payload)"
        @patch-table-field="(id, u) => emit('patch-table-field', id, u)"
        @builder-drag-end="emit('builder-drag-end')"
        @summary-field-settings="(p) => emit('summary-field-settings', p)"
      />
    </div>
    <VBtn
      class="pb-add-row-btn"
      variant="outlined"
      size="small"
      @click="emit('add-row')"
    >
      <VIcons type="add" />
      Добавить строку
    </VBtn>
  </div>
</template>

<style scoped lang="scss">
.pb-grid-rows {
  position: relative;
  z-index: 1;
}
</style>
